import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, CircularProgress, TextField, Typography } from "@mui/material";
import { MapOutlined as MapOutlinedIcon, MyLocation as MyLocationIcon, AddLocationAlt as AddLocationAltIcon, SaveOutlined as SaveOutlinedIcon } from "@mui/icons-material";
import AddressbookPanelShell from "./AddressbookPanelShell";

const LEAFLET_CSS_ID = "leaflet-css";
const LEAFLET_JS_ID = "leaflet-js";

const ensureLeaflet = async () => {
  if (typeof window === "undefined") return null;
  if (!document.getElementById(LEAFLET_CSS_ID)) {
    const css = document.createElement("link");
    css.id = LEAFLET_CSS_ID;
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);
  }
  if (!document.getElementById(LEAFLET_JS_ID)) {
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.id = LEAFLET_JS_ID;
      s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.body.appendChild(s);
    });
  }
  return window.L || null;
};

const toInterestLocations = (contact) => {
  const interests = (contact?.data && typeof contact.data === "object" && contact.data.interests) || contact?.interests || {};
  const rows = Array.isArray(interests?.geoLocations) ? interests.geoLocations : [];
  return rows
    .map((g) => ({
      latitude: Number(g?.latitude),
      longitude: Number(g?.longitude),
      radiusMiles: Number(g?.radiusMiles) || 5,
    }))
    .filter((g) => Number.isFinite(g.latitude) && Number.isFinite(g.longitude));
};

const ContactMapPanel = ({ contact, onClose, onSaveInterests, embedded = false }) => {
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const hasAutoCenteredRef = useRef(false);
  // Fit-to-pins should run once per contact (on first load of their saved pins).
  // After that, user actions (drop pin, save) must NOT move the map view.
  const hasFitToPinsRef = useRef(false);
  const [pins, setPins] = useState([]);
  const [selectedPinIdx, setSelectedPinIdx] = useState(null);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedPinsKey, setSavedPinsKey] = useState("[]");

  const headerTitle = useMemo(() => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <MapOutlinedIcon sx={{ fontSize: 16, color: "#1D4ED8" }} />
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Interest Map</Typography>
    </Box>
  ), []);

  useEffect(() => {
    if (!contact) {
      setPins([]);
      setSelectedPinIdx(null);
      return;
    }
    hasAutoCenteredRef.current = false;
    hasFitToPinsRef.current = false;
    const nextPins = toInterestLocations(contact);
    setPins(nextPins);
    setSavedPinsKey(JSON.stringify(nextPins));
    setSelectedPinIdx(nextPins.length ? 0 : null);
  }, [contact?.id]);

  const pinsKey = JSON.stringify(pins);
  const hasUnsavedChanges = pinsKey !== savedPinsKey;

  useEffect(() => {
    let cancelled = false;
    if (!contact || !mapElRef.current) return undefined;
    (async () => {
      const L = await ensureLeaflet();
      if (cancelled || !L || !mapElRef.current) return;
      if (!mapRef.current) {
        mapRef.current = L.map(mapElRef.current).setView([37.36, -121.93], 9);
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: "&copy; OpenStreetMap contributors",
        }).addTo(mapRef.current);
        markerLayerRef.current = L.layerGroup().addTo(mapRef.current);
        mapRef.current.on("click", (e) => {
          const { lat, lng } = e.latlng;
          setPins((prev) => {
            const next = [...prev, { latitude: lat, longitude: lng, radiusMiles: 5 }];
            setSelectedPinIdx(next.length - 1);
            return next;
          });
        });
      }
      mapRef.current.invalidateSize();
      setTimeout(() => mapRef.current?.invalidateSize(), 120);
    })();
    return () => {
      cancelled = true;
    };
  }, [contact]);

  useEffect(() => {
    if (!mapRef.current || !markerLayerRef.current || !window.L) return;
    const L = window.L;
    markerLayerRef.current.clearLayers();
    const bounds = L.latLngBounds([]);
    pins.forEach((p, idx) => {
      const lat = Number(p?.latitude);
      const lng = Number(p?.longitude);
      const radius = Number(p?.radiusMiles) || 1;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const marker = L.marker([lat, lng], {
        icon: L.divIcon({
          className: "pin-number-marker",
          html: `<div style="width:22px;height:22px;border-radius:999px;background:#2563EB;color:#fff;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid #ffffff;box-shadow:0 1px 4px rgba(0,0,0,0.25);">${idx + 1}</div>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        }),
      }).addTo(markerLayerRef.current);
      marker.bindTooltip(`Pin ${idx + 1}`);
      const circle = L.circle([lat, lng], {
        radius: radius * 1609.34,
        color: "#2563EB",
        fillColor: "#93C5FD",
        fillOpacity: 0.2,
        weight: 1.5,
      }).addTo(markerLayerRef.current);
      // Fit to full circle footprint so selected radius is visible.
      bounds.extend(circle.getBounds());
    });
    if (bounds.isValid() && !hasFitToPinsRef.current) {
      mapRef.current.fitBounds(bounds.pad(0.25));
      hasFitToPinsRef.current = true;
    }
  }, [pins]);

  const focusCurrentLocation = useCallback(() => {
    if (!navigator?.geolocation || !mapRef.current) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        mapRef.current.setView([lat, lng], 12, { animate: true });
      },
      () => {
        setSearchError("Could not get current location.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const locateAddress = useCallback(async () => {
    const q = String(search || "").trim();
    if (!q || !mapRef.current) return;
    setSearching(true);
    setSearchError("");
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`Search failed (${res.status})`);
      const rows = await res.json();
      const first = Array.isArray(rows) ? rows[0] : null;
      const lat = Number(first?.lat);
      const lon = Number(first?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) throw new Error("Address not found");
      mapRef.current.setView([lat, lon], 12, { animate: true });
    } catch (err) {
      setSearchError(err?.message || "Failed to locate address");
    } finally {
      setSearching(false);
    }
  }, [search]);

  useEffect(() => {
    if (!mapRef.current || !contact || hasAutoCenteredRef.current) return;
    hasAutoCenteredRef.current = true;
    focusCurrentLocation();
  }, [contact, focusCurrentLocation]);

  useEffect(() => {
    if (!mapRef.current || selectedPinIdx == null) return;
    const pin = pins[selectedPinIdx];
    const lat = Number(pin?.latitude);
    const lng = Number(pin?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    mapRef.current.panTo([lat, lng], { animate: true });
  }, [pins, selectedPinIdx]);

  const headerActions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, width: { xs: "100%", sm: 560 }, maxWidth: "100%" }}>
      <Button size="small" variant="outlined" startIcon={<MyLocationIcon />} onClick={focusCurrentLocation} sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>Current</Button>
      <TextField
        fullWidth
        size="small"
        placeholder="Address, city, or zip"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") locateAddress(); }}
        sx={{ minWidth: 0 }}
      />
      <Button
        size="small"
        variant="contained"
        startIcon={searching ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <AddLocationAltIcon />}
        onClick={locateAddress}
        disabled={searching}
        sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
      >
        Locate
      </Button>
    </Box>
  );
  const selectedRadius = selectedPinIdx != null ? Number(pins[selectedPinIdx]?.radiusMiles) || 5 : 5;
  const handleSavePins = useCallback(async () => {
    if (!hasUnsavedChanges || saving) return;
    setSaving(true);
    try {
      await Promise.resolve(onSaveInterests?.(contact, pins));
      setSavedPinsKey(JSON.stringify(pins));
    } finally {
      setSaving(false);
    }
  }, [contact, hasUnsavedChanges, onSaveInterests, pins, saving]);

  if (!contact) {
    const emptyBody = (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <Typography sx={{ color: "text.secondary", fontSize: 13 }}>Select a contact to use interest map.</Typography>
      </Box>
    );
    if (embedded) return emptyBody;
    return (
      <AddressbookPanelShell onClose={onClose} titleContent={headerTitle}>
        {emptyBody}
      </AddressbookPanelShell>
    );
  }

  const footerLeftContent = (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
          <Typography sx={{ color: "text.secondary", fontSize: 11, mr: 0.5 }}>
            {pins.length} pin{pins.length === 1 ? "" : "s"}
          </Typography>
          {pins.map((pin, idx) => (
            <Chip
              key={`pin-pill-${idx}`}
              size="small"
              label={`Pin ${idx + 1} • ${Math.round(Number(pin?.radiusMiles) || 0)} mi`}
              color={selectedPinIdx === idx ? "primary" : "default"}
              variant={selectedPinIdx === idx ? "filled" : "outlined"}
              onClick={() => setSelectedPinIdx(idx)}
              onDelete={() => setPins((prev) => {
                const next = prev.filter((__, i) => i !== idx);
                setSelectedPinIdx((cur) => {
                  if (next.length === 0) return null;
                  if (cur == null) return 0;
                  if (cur === idx) return Math.min(idx, next.length - 1);
                  if (cur > idx) return cur - 1;
                  return cur;
                });
                return next;
              })}
              sx={{ height: 20, "& .MuiChip-label": { px: 0.75, fontSize: 11 } }}
            />
          ))}
        </Box>
  );

  const footerRightContent = (
        <>
          {selectedPinIdx != null && (
            <TextField
              size="small"
              select
              label="Radius"
              value={selectedRadius}
              onChange={(e) => {
                const nextRadius = Number(e.target.value) || 5;
                setPins((prev) => prev.map((p, i) => (i === selectedPinIdx ? { ...p, radiusMiles: nextRadius } : p)));
              }}
              sx={{ width: 96 }}
              SelectProps={{ native: true }}
            >
              {[1, 2, 3, 5, 8, 10, 15, 20, 30, 40, 50].map((mi) => (
                <option key={mi} value={mi}>{mi} mi</option>
              ))}
            </TextField>
          )}
          <Button
            size="small"
            color="error"
            onClick={() => {
              setPins([]);
              setSelectedPinIdx(null);
            }}
          >
            Clear
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSavePins}
            disabled={!hasUnsavedChanges || saving}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={!hasUnsavedChanges ? { backgroundColor: "#94A3B8", color: "#fff", "&.Mui-disabled": { backgroundColor: "#94A3B8", color: "#fff", opacity: 1 } } : undefined}
          >
            {saving ? "Saving..." : hasUnsavedChanges ? "Save Pins" : "Saved"}
          </Button>
        </>
  );

  const mapBody = (
    <>
      {searchError ? <Typography sx={{ fontSize: 12, color: "#DC2626", mb: 1 }}>{searchError}</Typography> : null}
      <Box ref={mapElRef} sx={{ width: "100%", minHeight: embedded ? 320 : 560, height: embedded ? 360 : "100%", borderRadius: 1, border: "1px solid #E2E8F0" }} />
    </>
  );

  if (embedded) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {headerActions}
        </Box>
        {mapBody}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "space-between" }}>
          {footerLeftContent}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {footerRightContent}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <AddressbookPanelShell
      onClose={onClose}
      titleContent={headerTitle}
      headerActions={headerActions}
      footerLeft={footerLeftContent}
      footerRight={footerRightContent}
    >
      {mapBody}
    </AddressbookPanelShell>
  );
};

export default ContactMapPanel;
